import React from "react";
import styled, { css, keyframes } from "styled-components";
import { HugeiconsIcon } from "@hugeicons/react";
import type { IconSvgElement } from "@hugeicons/react";

type Props = {
  /** An icon from @hugeicons/core-free-icons. */
  icon: IconSvgElement;
  /** Edge length in px. A fontSize in `style` overrides it, see below. */
  size?: number;
  /** Heavier only where an icon has to hold its own against large type. */
  strokeWidth?: number;
  /** Turns, for a busy or loading indicator. */
  spin?: boolean;
  /** Degrees, for a caret that points along whatever it opens. */
  rotate?: number;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<SVGSVGElement>;
  className?: string;
  /** Describes the icon to a screen reader. Leave unset for decoration. */
  title?: string;
};

const spinning = keyframes`
  to { transform: rotate(360deg); }
`;

const Svg = styled(HugeiconsIcon)<{ $spin?: boolean; $rotate?: number }>`
  /* Not block: an icon often sits inline with its label, and a block box
     would drop it onto its own line. Inline-block still keeps it off the
     text baseline, where an inline SVG rides about 2px low. Inside a flex
     row it is blockified anyway, so a centred segment stays centred. */
  display: inline-block;
  flex: none;
  vertical-align: middle;

  ${({ $spin }) =>
    $spin &&
    css`
      animation: ${spinning} 1s linear infinite;
    `}

  ${({ $rotate }) =>
    $rotate
      ? css`
          transform: rotate(${$rotate}deg);
          transition: transform 0.2s ease;
        `
      : ""}
`;

/** A length in `style` as a number of px, or undefined if it is not one. */
const toPx = (value: string | number | undefined): number | undefined => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

/**
 * The one place icons are drawn.
 *
 * Hugeicons' free set is Stroke Rounded: an even 1.5px line on a 24px grid,
 * which is the plain, professional end of the family rather than the solid,
 * duotone or bulk styles. Every glyph strokes in currentColor, so an icon
 * keeps taking its colour from whatever it sits in, the way the Ant Design
 * icons it replaces did.
 *
 * A fontSize in `style` sets the icon's size. Ant Design's icons are sized
 * that way, being 1em square, and a good number of call sites do it — so
 * honouring it here means those sites keep working and read the same as the
 * rest of the codebase.
 */
const HugeIcon: React.FC<Props> = ({
  icon,
  size = 14,
  strokeWidth = 1.5,
  spin,
  rotate,
  style,
  onClick,
  className,
  title,
}) => (
  <Svg
    icon={icon}
    size={toPx(style?.fontSize) ?? size}
    strokeWidth={strokeWidth}
    style={style}
    onClick={onClick}
    className={className}
    $spin={spin}
    $rotate={rotate}
    aria-label={title}
    aria-hidden={title ? undefined : true}
    focusable={false}
  />
);

export default HugeIcon;
